import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../App'
import { supabase } from '../../lib/supabase'
import { subscribeToWearLogChanges } from '../../lib/wearLogEvents'
import {
  LAYERING_FEEDBACK_OPTIONS,
  findSuggestedPairingForWear,
  loadVolatilityBridgeNote,
  pairingIdentity,
  writePairingFeedback,
} from '../../lib/layeringFeedback'

function dismissedKey(wear, pairingRow, pairing) {
  return `fos_layering_feedback_dismissed:${wear?.id || ''}:${pairingRow?.id || ''}:${pairingIdentity(pairing)}`
}

function wasDismissed(key) {
  try {
    return window.localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function rememberDismissed(key) {
  try {
    window.localStorage.setItem(key, '1')
  } catch {
    // Storage can be unavailable in private mode; dismissal still works for
    // the mounted session through local state.
  }
}

export default function LayeringFeedbackPrompt() {
  const { user } = useAuth()
  const [pending, setPending] = useState(null)
  const [saving, setSaving] = useState('')
  const [error, setError] = useState('')
  const requestVersion = useRef(0)

  useEffect(() => subscribeToWearLogChanges(change => {
    const wear = change?.row
    if (!user?.id || !wear || !['created', 'updated'].includes(change.type)) return
    if (!Array.isArray(wear.layered_with) || wear.layered_with.length === 0) return
    const version = ++requestVersion.current

    void Promise.all([
      supabase
        .from('pairings')
        .select('id,user_id,fragrance_id,suggested_pairings,feedback,feedback_at,volatility_bridge_note')
        .eq('user_id', user.id)
        .eq('fragrance_id', wear.fragrance_id),
      supabase
        .from('collection_display')
        .select('id,user_id,catalog_id,name,house')
        .eq('id', wear.fragrance_id)
        .eq('user_id', user.id)
        .maybeSingle(),
    ]).then(([pairingResult, anchorResult]) => {
      if (version !== requestVersion.current) return
      if (pairingResult.error) throw pairingResult.error
      if (anchorResult.error) throw anchorResult.error
      const match = findSuggestedPairingForWear(wear, pairingResult.data)
      if (!match) return
      const key = dismissedKey(wear, match.pairingRow, match.pairing)
      if (wasDismissed(key)) return
      setError('')
      const prompt = { wear, anchor: anchorResult.data, ...match, dismissedKey: key }
      setPending(prompt)
      void loadVolatilityBridgeNote(supabase, {
        anchorCatalogId: anchorResult.data?.catalog_id,
        partnerCatalogId: match.pairing.catalog_id,
      }).then(volatilityBridgeNote => {
        if (version !== requestVersion.current) return
        setPending(current => current?.dismissedKey === key
          ? { ...current, volatilityBridgeNote }
          : current)
      }).catch(() => {
        // The write path retries this evidence read. Until then the prompt
        // simply abstains from showing an explanation.
      })
    }).catch(() => {
      // Discovery is an optional post-save enhancement. A read failure must
      // never turn a committed wear into an error or reopen its capture sheet.
    })
  }), [user?.id])

  const dismiss = () => {
    if (pending?.dismissedKey) rememberDismissed(pending.dismissedKey)
    setPending(null)
    setError('')
  }

  const submit = async (value) => {
    if (!pending || saving) return
    setSaving(value)
    setError('')
    try {
      const volatilityBridgeNote = pending.volatilityBridgeNote !== undefined
        ? pending.volatilityBridgeNote
        : await loadVolatilityBridgeNote(supabase, {
            anchorCatalogId: pending.anchor?.catalog_id,
            partnerCatalogId: pending.pairing.catalog_id,
          })
      await writePairingFeedback(supabase, {
        pairingRow: pending.pairingRow,
        pairing: pending.pairing,
        userId: user.id,
        value,
        volatilityBridgeNote,
      })
      dismiss()
    } catch (saveError) {
      setError(saveError?.message || 'Could not save that feedback. Tap again to retry.')
    } finally {
      setSaving('')
    }
  }

  if (!pending) return null

  return (
    <aside
      className="fixed left-4 right-4 z-[55] mx-auto max-w-lg rounded-2xl border border-gold/35 bg-charcoal-700 p-4 shadow-2xl"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5.5rem)' }}
      aria-label="Layering feedback"
      data-layering-feedback-prompt
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-cream">How did the layers wear?</p>
          <p className="mt-0.5 text-xs text-cream/50">
            {pending.wear.fragrance_name} + {pending.pairing.name}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss layering feedback"
          className="flex h-8 w-8 shrink-0 items-center justify-center text-xl text-cream/50"
        >
          ×
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {LAYERING_FEEDBACK_OPTIONS.map(option => (
          <button
            key={option.value}
            type="button"
            disabled={Boolean(saving)}
            onClick={() => submit(option.value)}
            className="pill border border-white/15 px-3 py-1.5 text-xs text-cream/75 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-50"
          >
            {saving === option.value ? 'Saving…' : option.label}
          </button>
        ))}
      </div>
      {pending.volatilityBridgeNote && (
        <p className="mt-3 border-t border-white/8 pt-3 text-xs leading-relaxed text-sage">
          {pending.volatilityBridgeNote}
        </p>
      )}
      {error && <p className="mt-2 text-xs text-red-300" role="alert">{error}</p>}
    </aside>
  )
}
